import { client } from './api';
import React, { PropsWithChildren } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import * as Sentry from '@sentry/react';
import { trackEvent } from 'shared-utils/analytics';
import { AuthContext, User } from './AuthContext';

const AUTH_LOAD_SLOW_THRESHOLD_MS = 15000;

export { AuthContext } from './AuthContext';
export type { User, UserDefaultFilters, AuthContextType } from './AuthContext';

export const LoginProvider = (props: PropsWithChildren) => {
  const { isLoading, isError, data } = useQuery({
    queryKey: ['checkLogin'],
    queryFn: () => client.get('info').then((res) => res.data),
    retry: false,
  });

  const queryClient = useQueryClient();
  // TODO: verify this is needed
  const [localUser, setLocalUser] = React.useState<User | null>(data?.user || null);

  // Update local user when query data changes
  React.useEffect(() => {
    if (isError) {
      Sentry.setUser(null);
    } else if (data?.user) {
      setLocalUser(data.user);
      Sentry.setUser({
        id: data.user.member_id,
        email: data.user.email,
      });
      trackEvent('partner_portal_user_info_loaded', {});
    }
  }, [isError, data?.user]);

  // Track when the auth loading screen is visible for a long time
  React.useEffect(() => {
    if (!isLoading) return;
    const timeoutId = window.setTimeout(() => {
      trackEvent('partner_portal_auth_load_slow', {
        threshold_ms: String(AUTH_LOAD_SLOW_THRESHOLD_MS),
      });
    }, AUTH_LOAD_SLOW_THRESHOLD_MS);
    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  let value;
  if (isError) {
    value = { auth: false, user: null, loaded: true, setUser: () => {} };
  } else {
    value = {
      auth: true,
      user: localUser,
      loaded: true,
      setUser: (user: User | null) => {
        setLocalUser(user);
        queryClient.setQueryData(['checkLogin'], { user });
      },
    };
  }

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};

export const LoginRequired = (props: PropsWithChildren) => {
  const authContext = React.useContext(AuthContext);
  const featureFlags = authContext.user?.feature_flags;
  const location = useLocation();

  if (authContext.auth === false) {
    const urlParams = new URLSearchParams(location.search);
    const org = urlParams.get('org');
    if (org) {
      urlParams.delete('org');
    }

    const loginPath = org ? `/login/${org}` : `/login`;

    let next = location.pathname;
    if (urlParams.toString() !== '') {
      next = `${next}?${urlParams.toString()}`;
    }

    let redirectUrl;
    if (next === '/') {
      redirectUrl = `${loginPath}`;
    } else {
      redirectUrl = `${loginPath}?next=${encodeURIComponent(next)}`;
    }
    return <Navigate to={redirectUrl} />;
  }

  if (
    featureFlags?.self_serve_onboarding === true &&
    authContext.user?.account_setup_complete === false &&
    !location.pathname.startsWith('/account-setup')
  ) {
    // If the user is not fully set up, redirect to the account setup page
    return <Navigate to="/account-setup" />;
  }

  return <>{props.children}</>;
};
